import { useCallback, useMemo } from 'react';
import {
  DataTable,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  FormattedDateTime,
  InlineStack,
  Link,
  Spinner,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { createColumnHelper, type Row, type Table as TanstackTable } from '@tanstack/table-core';
import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { isEmptyString } from '@/utils/string';
import { importIsInvalid, type Collection } from '@/services/db/collection/entity';
import { useAnalytics } from '@/services/analytics';
import { getCollectionAnalyticsProperties } from './collection-analytics';
import { CollectionUsageCell } from './collection-usage-cell';
import { CollectionImportErrorBanner } from './collection-import-error-banner';
import { useCollectionImportErrorRecovery } from './import-error-recovery';
import { useCollectionFormModal } from './collection-form-modal';
import { useCollectionDelete } from './use-collection-delete';

const columnHelper = createColumnHelper<Collection>();

/**
 * A collection only has a detail view worth linking to once its import has landed: while it is
 * importing there is nothing to show yet, and the detail route redirects a failed import back
 * to the list.
 */
function hasReachableDetail(collection: Collection): boolean {
  return !collection.importing && !importIsInvalid(collection);
}

type NameCellProps = {
  canDelete: boolean;
  canEdit: boolean;
  collection: Collection;
  maxFileSizeBytes: number | undefined;
  onInvalidImportRetry?: (collection: Collection) => void;
  workspaceId: number;
};

function NameCell({
  canDelete,
  canEdit,
  collection,
  maxFileSizeBytes,
  onInvalidImportRetry,
  workspaceId,
}: NameCellProps) {
  const router = useRouter();
  const collectionModal = useCollectionFormModal();
  const analytics = useAnalytics();
  const importErrorActions = useCollectionImportErrorRecovery({
    collection,
    workspaceId,
    canDelete,
    onDiscarded: onInvalidImportRetry,
    onReimport: () => {
      void analytics.track('collections', 'updateStarted', getCollectionAnalyticsProperties(collection));
      collectionModal.open({ workspaceId, collection, maxFileSizeBytes });
    },
  });
  const collectionDetailHref = router.buildLocation({
    to: '/workspaces/$workspaceId/journeys/collections/$collectionId',
    params: { workspaceId, collectionId: collection.id },
  }).href;

  const nameContent = collection.importing ? (
    <InlineStack alignBlock="center" gap="100">
      <Spinner />
      <Text>{collection.name}</Text>
      <Text color="subtle">Processing…</Text>
    </InlineStack>
  ) : importIsInvalid(collection) ? (
    <InlineStack alignBlock="center" gap="100">
      <Text>{collection.name}</Text>
      <Text color="critical">Failed</Text>
    </InlineStack>
  ) : (
    <Link href={collectionDetailHref}>{collection.name}</Link>
  );

  return (
    <Stack gap="050">
      {nameContent}
      {!isEmptyString(collection.description) ? <Text color="subtle">{collection.description}</Text> : null}
      {!collection.importing && (collection.import_error?.length ?? 0) > 0 ? (
        <CollectionImportErrorBanner collection={collection} canEdit={canEdit} {...importErrorActions} />
      ) : null}
    </Stack>
  );
}

function createColumns({
  canDelete,
  canEdit,
  getCollectionDetailHref,
  maxFileSizeBytes,
  onInvalidImportRetry,
  workspaceId,
}: {
  canDelete: boolean;
  canEdit: boolean;
  getCollectionDetailHref: (collectionId: string) => string;
  maxFileSizeBytes: number | undefined;
  onInvalidImportRetry?: (collection: Collection) => void;
  workspaceId: number;
}) {
  return [
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Name',
      enableSorting: true,
      meta: { width: '2fr', minWidth: 240 },
      cell: (info) => (
        <NameCell
          canDelete={canDelete}
          canEdit={canEdit}
          collection={info.row.original}
          maxFileSizeBytes={maxFileSizeBytes}
          onInvalidImportRetry={onInvalidImportRetry}
          workspaceId={workspaceId}
        />
      ),
    }),
    columnHelper.accessor('rows', {
      id: 'rows',
      header: 'Items',
      enableSorting: true,
      meta: { minWidth: 120 },
      cell: (info) => {
        const collection = info.row.original;
        if (!hasReachableDetail(collection)) {
          return <Text>{info.getValue()}</Text>;
        }
        return (
          <Link href={getCollectionDetailHref(collection.id)} variant="secondary">
            {info.getValue()}
          </Link>
        );
      },
    }),
    // Not sortable: the counts arrive from a separate batch read, so they aren't on the row
    // for the client-side sort to compare.
    columnHelper.display({
      id: 'usage',
      header: 'Used by',
      meta: { minWidth: 140 },
      cell: (info) => <CollectionUsageCell collectionId={info.row.original.id} workspaceId={workspaceId} />,
    }),
    columnHelper.accessor('updated_at', {
      id: 'updated_at',
      header: 'Last Updated',
      enableSorting: true,
      meta: { minWidth: 220 },
      cell: (info) => <FormattedDateTime date={info.getValue()} format="medium_with_time" withTooltip />,
    }),
  ];
}

type CollectionsEmptyStateProps = {
  table: TanstackTable<Collection>;
};

// The DataTable renders this whenever no rows are visible; here that only
// happens when a client-side search filters everything out (the page shows a
// richer hero when the workspace has zero collections).
function CollectionsSearchEmptyState({ table }: CollectionsEmptyStateProps) {
  const globalFilter = table.getState().globalFilter;
  const search = typeof globalFilter === 'string' ? globalFilter : '';

  return (
    <EmptyState>
      <EmptyStateTitle icon="search">No collections found</EmptyStateTitle>
      <EmptyStateDescription>
        {isEmptyString(search) ? 'No collections to show.' : `No collections match "${search}".`}
      </EmptyStateDescription>
    </EmptyState>
  );
}

type CollectionsTableProps = {
  collections: readonly Collection[];
  maxFileSizeBytes: number | undefined;
  onInvalidImportRetry?: (collection: Collection) => void;
  onSortingChange?: (sorting: CollectionsTableSorting) => void;
  sorting?: CollectionsTableSorting;
  workspaceId: number;
};

export type CollectionsTableSorting = {
  id: string;
  desc: boolean;
};

export function CollectionsTable({
  collections,
  maxFileSizeBytes,
  onInvalidImportRetry,
  onSortingChange,
  sorting,
  workspaceId,
}: CollectionsTableProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('journeys/collections/edit') === true;
  const canDelete = hasPermission('journeys/collections/delete') === true;

  const collectionModal = useCollectionFormModal();
  const { requestDelete, inUseTooltip } = useCollectionDelete({ workspaceId });
  const router = useRouter();
  const analytics = useAnalytics();
  const getCollectionDetailHref = useCallback(
    (collectionId: string) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/journeys/collections/$collectionId',
        params: { workspaceId, collectionId },
      }).href,
    [router, workspaceId],
  );

  const columns = useMemo(
    () =>
      createColumns({
        canDelete,
        canEdit,
        getCollectionDetailHref,
        maxFileSizeBytes,
        onInvalidImportRetry,
        workspaceId,
      }),
    [canDelete, canEdit, getCollectionDetailHref, maxFileSizeBytes, onInvalidImportRetry, workspaceId],
  );
  const data = useMemo(() => [...collections], [collections]);
  const sortingConfig = useMemo(
    () =>
      sorting === undefined || onSortingChange === undefined
        ? undefined
        : {
            value: sorting,
            onChange: onSortingChange,
            isManual: true,
          },
    [onSortingChange, sorting],
  );

  return (
    <DataTable
      caption="Collections"
      data={data}
      columns={columns}
      getRowId={(collection) => collection.id}
      shouldHighlightOnHover
      withSorting
      sorting={sortingConfig}
      withSearch
      search={{
        ariaLabel: 'Collection name',
        placeholder: 'Find by collection name',
        shouldSearchOnInput: true,
        shouldSearchOnClear: true,
      }}
      emptyStateComponent={CollectionsSearchEmptyState}
      rowActions={(row: Row<Collection>) => {
        // Ember hides the trigger outright rather than showing an all-disabled menu, but a
        // role with delete-only access still needs its one action — the detail header offers
        // Delete on `canDelete` alone, so the list view must agree.
        if (!canEdit && !canDelete) {
          return {};
        }

        const collection = row.original;
        return {
          dropdownTrigger: { ariaLabel: `Open actions for ${collection.name}` },
          dropdownActions: [
            {
              label: 'Update collection',
              icon: 'edit',
              isDisabled: !canEdit || collection.importing,
              onClick: () => {
                void analytics.track('collections', 'updateStarted', getCollectionAnalyticsProperties(collection));
                collectionModal.open({ workspaceId, collection, maxFileSizeBytes });
              },
            },
            {
              label: 'Delete collection',
              icon: 'delete-trash',
              isDanger: true,
              isDisabled: !canDelete || collection.in_use || collection.importing,
              tooltip: collection.in_use ? inUseTooltip : undefined,
              onClick: () => void requestDelete(collection),
            },
          ],
        };
      }}
    />
  );
}
